import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { Link } from "react-router";
import type { FeedSegment, FeedSlideWire, FeedTileWire, Product } from "@/api";
import { ChevronLeftIcon, ChevronRight20Icon, ChevronRightIcon } from "@/components/icons";
import { ProductCard } from "@/components/product/product-card";
import { HOME_SEGMENTS } from "@/lib/home-feed";
import { expandProductTiles } from "@/lib/product-tiles";
import { cn } from "@/lib/utils";

// Section renderers for the unified home feed. Each mirrors a `kind` from the
// gateway's /feed/blocks contract and the concept of the matching mobile
// component. A block whose content cannot be rendered stands itself down
// (returns null) rather than drawing an empty titled strip — same as the app.

// A horizontal rail that bleeds to the screen edge on mobile (so cards run
// edge-to-edge) and snaps back into the content column on desktop. The
// scrollbar is hidden — the content peeking past the edge is the affordance.
const RAIL_CLASS = "-mx-4 flex snap-x scroll-px-4 gap-3 overflow-x-auto px-4 pb-1 md:mx-0 md:scroll-px-0 md:px-0 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden";

// Where a tile / slide points: a collection page, a category-filtered catalog,
// or the catalog root as a last resort.
function targetHref(t: {
  categoryId?: string;
  collectionSlug?: string;
}): string {
  if (t.collectionSlug) return `/feed/${t.collectionSlug}`;
  if (t.categoryId) return `/catalog?categoryId=${encodeURIComponent(t.categoryId)}`;
  return "/catalog";
}

// The shared section heading — title + optional description on the left and,
// when the section is a scrollable rail, a chevron affordance on the right
// (matches the Figma «Идеи OSMIX» / «Название подборки» headers). Styled the
// same wherever it appears so the page keeps a single visual rhythm.
function SectionHeader(t0) {
  const $ = _c(12);
  const {
    title,
    description,
    chevronHref
  } = t0;
  if (!title && !description) {
    return null;
  }
  let t1;
  if ($[0] !== title) {
    t1 = title && <h2 className="text-[16px] font-normal leading-5 text-[#1F1F1F] sm:text-[20px] sm:font-semibold sm:leading-[23px]">{title}</h2>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== description) {
    t2 = description && <p className="text-[12px] leading-4 text-[#1F1F1F] sm:text-[16px] sm:leading-5">{description}</p>;
    $[2] = description;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex flex-col sm:gap-1.5">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== chevronHref) {
    t4 = chevronHref && <Link to={chevronHref} aria-label={"\u0421\u043C\u043E\u0442\u0440\u0435\u0442\u044C \u0432\u0441\u0435"} className="flex size-8 shrink-0 items-center justify-center rounded-full text-ds-primary transition-colors hover:bg-ds-hover hover:text-ds-accent"><ChevronRight20Icon /></Link>;
    $[7] = chevronHref;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <div className="flex items-end justify-between gap-4">{t3}{t4}</div>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}

// ── Gender / segment tabs ──────────────────────────────────────────────────
// Centered underlined tabs (Figma «Таб»): под всей группой — линия 1px #1F1F1F 8 %,
// под активной вкладкой — tangelo 2px r16. Desktop: 500/18, пункт 56px (pad-top 10),
// зазор 40; Tablet/Mobile: 500/16, пункт 40px (pad-top 8), зазор 24. Неактивные —
// #1F1F1F 48 %. Sits just under the hero.
export function HomeGenderTabs(t0) {
  const $ = _c(5);
  const {
    active,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== active || $[1] !== onSelect) {
    t1 = HOME_SEGMENTS.map(s => {
      const selected = s.key === active;
      return <button key={s.key} type="button" role="tab" aria-selected={selected} onClick={() => onSelect(s.key)} className={cn("relative h-10 pt-2 text-center text-[16px] font-medium leading-[21px] outline-none transition-colors focus-visible:text-osmix-tangelo lg:h-14 lg:pt-2.5 lg:text-[18px] lg:leading-[23px]", selected ? "text-[#1F1F1F]" : "text-[#1F1F1F]/48 hover:text-[#1F1F1F]/70")}>{s.label}<span className={cn("absolute inset-x-0 -bottom-px h-0.5 rounded-[16px] bg-osmix-tangelo transition-opacity", selected ? "opacity-100" : "opacity-0")} /></button>;
    });
    $[0] = active;
    $[1] = onSelect;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <div className="mx-auto flex w-fit justify-center gap-6 border-b border-[#1F1F1F]/[0.08] lg:gap-10" role="tablist" aria-label={"\u0410\u0443\u0434\u0438\u0442\u043E\u0440\u0438\u044F"}>{t1}</div>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

// ── campaign → hero carousel ───────────────────────────────────────────────
// `bleed` renders the full-width square-cornered page hero (Figma home top);
// without it the same component is a rounded card for an injected campaign.
export function HeroCampaign(t0) {
  const $ = _c(10);
  const {
    slides,
    bleed: t1
  } = t0;
  const bleed = t1 === undefined ? false : t1;
  const [index, setIndex] = useState(0);
  let t2;
  let t3;
  if ($[0] !== bleed || $[1] !== index || $[2] !== slides) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const usable = slides.filter(_temp);
      if (usable.length === 0) {
        t3 = null;
        break bb0;
      }
      const current = Math.min(index, usable.length - 1);
      const slide = usable[current];
      if (!slide) {
        t3 = null;
        break bb0;
      }
      const go = next => setIndex((next + usable.length) % usable.length);
      const t4 = bleed ? "rounded-none" : "rounded-[4px]";
      let t5;
      if ($[5] !== t4) {
        t5 = cn("group/hero relative overflow-hidden bg-osmix-gray-100", t4);
        $[5] = t4;
        $[6] = t5;
      } else {
        t5 = $[6];
      }
      const t6 = targetHref(slide);
      const t7 = bleed ? "aspect-[420/352] sm:aspect-[744/418] lg:aspect-[1200/674] lg:max-h-[min(66svh,674px)]" : "aspect-[4/3] sm:aspect-[16/9] lg:aspect-[21/9]";
      let t8;
      if ($[7] !== t7) {
        t8 = cn("relative w-full", t7);
        $[7] = t7;
        $[8] = t8;
      } else {
        t8 = $[8];
      }
      let t9;
      if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />;
        $[9] = t9;
      } else {
        t9 = $[9];
      }
      t2 = <div className={t5}><Link to={t6} className="group block"><div className={t8}><img src={slide.imageUrl} alt={slide.title ?? ""} className="size-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />{t9}<div className="absolute right-0 bottom-0 left-0 flex flex-col items-center gap-2 p-6 pb-10 text-center sm:p-8 md:p-10">{slide.title && <h2 className="max-w-2xl text-[26px] font-bold leading-[30px] text-white! drop-shadow-sm lg:text-[32px] lg:font-semibold lg:leading-[34px]">{slide.title}</h2>}{slide.description && <p className="max-w-xl text-[16px] leading-5 text-white drop-shadow-sm lg:text-[20px] lg:leading-[23px]">{slide.description}</p>}</div></div></Link>{usable.length > 1 && <><button type="button" aria-label={"\u041F\u0440\u0435\u0434\u044B\u0434\u0443\u0449\u0438\u0439 \u0441\u043B\u0430\u0439\u0434"} onClick={() => go(current - 1)} className="-translate-y-1/2 absolute top-1/2 left-3 flex size-10 items-center justify-center rounded-full bg-white/80 text-osmix-text-1 shadow-md backdrop-blur transition-all hover:bg-white sm:opacity-0 sm:group-hover/hero:opacity-100"><ChevronLeftIcon size={20} /></button><button type="button" aria-label={"\u0421\u043B\u0435\u0434\u0443\u044E\u0449\u0438\u0439 \u0441\u043B\u0430\u0439\u0434"} onClick={() => go(current + 1)} className="-translate-y-1/2 absolute top-1/2 right-3 flex size-10 items-center justify-center rounded-full bg-white/80 text-osmix-text-1 shadow-md backdrop-blur transition-all hover:bg-white sm:opacity-0 sm:group-hover/hero:opacity-100"><ChevronRightIcon size={20} /></button><div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-2">{usable.map((s_0, i) => <button key={s_0.id} type="button" aria-label={`Слайд ${i + 1}`} onClick={() => setIndex(i)} className={cn("size-1.5 rounded-full transition-colors", i === current ? "bg-white/65" : "bg-white/25 hover:bg-white/50")} />)}</div></>}</div>;
    }
    $[0] = bleed;
    $[1] = index;
    $[2] = slides;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  return t2;
}

// ── categoryRow → row of category tiles ────────────────────────────────────
// Кадр «Категории»: круглые плитки (r1000, подложка #F8F8F8) 135 (Desktop),
// 120 (Tablet 3840:59825), 85 (Mobile 2871:30391); подпись 400/14/18 #000 (на
// телефоне 400/12/16) через 5px; ряд по центру, зазор 20 / 12 / 10. От заголовка
// до ряда — 20 на Desktop, 10 на телефоне. Scrolls edge-to-edge on mobile.
function _temp(s) {
  return s.imageUrl;
}
export function CategoryRow(t0) {
  const $ = _c(18);
  const {
    title,
    description,
    items
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== description || $[1] !== items || $[2] !== title) {
    t5 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const usable = items.filter(_temp2);
      if (usable.length === 0) {
        t5 = null;
        break bb0;
      }
      t3 = "flex flex-col gap-2.5 lg:gap-5";
      if ($[8] !== description || $[9] !== title) {
        t4 = <SectionHeader title={title} description={description} />;
        $[8] = description;
        $[9] = title;
        $[10] = t4;
      } else {
        t4 = $[10];
      }
      t1 = "-mx-4 flex snap-x scroll-px-4 justify-center-safe gap-2.5 overflow-x-auto px-4 pb-1 sm:gap-3 md:mx-0 md:scroll-px-0 md:px-0 lg:gap-5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden";
      t2 = usable.map(_temp3);
    }
    $[0] = description;
    $[1] = items;
    $[2] = title;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
  }
  if (t5 !== Symbol.for("react.early_return_sentinel")) {
    return t5;
  }
  let t6;
  if ($[11] !== t1 || $[12] !== t2) {
    t6 = <div className={t1}>{t2}</div>;
    $[11] = t1;
    $[12] = t2;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t3 || $[15] !== t4 || $[16] !== t6) {
    t7 = <section className={t3}>{t4}{t6}</section>;
    $[14] = t3;
    $[15] = t4;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
}

// ── ideaRail → horizontal rail of inspiration tiles ────────────────────────
function _temp3(tile) {
  return <Link key={tile.id} to={targetHref(tile)} className="group flex w-[85px] shrink-0 snap-start flex-col items-center gap-[5px] outline-none sm:w-[120px] lg:w-[135px]"><div className="aspect-square w-full overflow-hidden rounded-full bg-[#F8F8F8]"><img src={tile.imageUrl} alt={tile.label ?? ""} loading="lazy" className="size-full object-cover transition-transform duration-300 group-hover:scale-105" /></div>{tile.label && <span className="line-clamp-2 text-center text-[12px] leading-4 text-black transition-colors group-hover:text-osmix-tangelo sm:text-[14px] sm:leading-[18px]">{tile.label}</span>}</Link>;
}
function _temp2(t) {
  return t.imageUrl;
}
export function IdeaRail(t0) {
  const $ = _c(19);
  const {
    title,
    description,
    items
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== description || $[1] !== items || $[2] !== title) {
    t5 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const usable = items.filter(_temp4);
      if (usable.length === 0) {
        t5 = null;
        break bb0;
      }
      t3 = "flex flex-col gap-2.5";
      if ($[8] !== description || $[9] !== title) {
        t4 = <SectionHeader title={title} description={description} chevronHref="/ideas" />;
        $[8] = description;
        $[9] = title;
        $[10] = t4;
      } else {
        t4 = $[10];
      }
      if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = cn(RAIL_CLASS, "gap-2 sm:gap-2.5 lg:gap-5");
        $[11] = t1;
      } else {
        t1 = $[11];
      }
      t2 = usable.map(_temp5);
    }
    $[0] = description;
    $[1] = items;
    $[2] = title;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
  }
  if (t5 !== Symbol.for("react.early_return_sentinel")) {
    return t5;
  }
  let t6;
  if ($[12] !== t1 || $[13] !== t2) {
    t6 = <div className={t1}>{t2}</div>;
    $[12] = t1;
    $[13] = t2;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t3 || $[16] !== t4 || $[17] !== t6) {
    t7 = <section className={t3}>{t4}{t6}</section>;
    $[15] = t3;
    $[16] = t4;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
}

// ── collectionMosaic → 1 + 4 mosaic of tiles ───────────────────────────────
function _temp5(tile) {
  return <Link key={tile.id} to={targetHref(tile)} aria-label={tile.label} className="group flex w-[119px] shrink-0 snap-start flex-col outline-none focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40 sm:w-[228px] lg:w-[270px]"><div className="aspect-[270/359] w-full overflow-hidden rounded-[4px] bg-osmix-gray-100"><img src={tile.imageUrl} alt={tile.label ?? ""} loading="lazy" className="size-full object-cover transition-transform duration-300 group-hover:scale-105" /></div></Link>;
}
function _temp4(t) {
  return t.imageUrl;
}
function MosaicTile(t0) {
  const $ = _c(15);
  const {
    tile,
    className,
    hero: t1
  } = t0;
  const hero = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== tile) {
    t2 = targetHref(tile);
    $[0] = tile;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== className) {
    t3 = cn("group relative overflow-hidden rounded-[4px] bg-osmix-gray-100 outline-none", className);
    $[2] = className;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = tile.label ?? "";
  let t5;
  if ($[4] !== t4 || $[5] !== tile.imageUrl) {
    t5 = <img src={tile.imageUrl} alt={t4} loading="lazy" className="size-full object-cover transition-transform duration-300 group-hover:scale-105" />;
    $[4] = t4;
    $[5] = tile.imageUrl;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== hero || $[8] !== tile.label) {
    t6 = tile.label && <span className={cn("absolute line-clamp-1 text-[12px] font-semibold leading-[15px] text-white drop-shadow-sm sm:bottom-2.5 sm:left-2.5 sm:max-w-[calc(100%-1.25rem)] sm:text-[16px] sm:leading-5 lg:bottom-5 lg:left-5 lg:max-w-[calc(100%-2.5rem)]", hero ? "bottom-1.5 left-1.5 max-w-[calc(100%-0.75rem)]" : "bottom-[3px] left-[3px] max-w-[calc(100%-6px)]")}>{tile.label}</span>;
    $[7] = hero;
    $[8] = tile.label;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t2 || $[11] !== t3 || $[12] !== t5 || $[13] !== t6) {
    t7 = <Link to={t2} className={t3}>{t5}{t6}</Link>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}
export function CollectionMosaic(t0) {
  const $ = _c(20);
  const {
    title,
    description,
    items
  } = t0;
  let t1;
  if ($[0] !== items) {
    t1 = items.filter(_temp6);
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const usable = t1;
  if (usable.length < 5) {
    return null;
  }
  let hero;
  let rest;
  if ($[2] !== usable) {
    [hero, ...rest] = usable;
    $[2] = usable;
    $[3] = hero;
    $[4] = rest;
  } else {
    hero = $[3];
    rest = $[4];
  }
  if (!hero) {
    return null;
  }
  let t2;
  if ($[5] !== rest) {
    t2 = rest.slice(0, 4);
    $[5] = rest;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const quad = t2;
  let t3;
  if ($[7] !== description || $[8] !== title) {
    t3 = <SectionHeader title={title} description={description} />;
    $[7] = description;
    $[8] = title;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== hero) {
    t4 = <MosaicTile tile={hero} hero={true} className="col-span-2 row-span-2 aspect-square" />;
    $[10] = hero;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== quad) {
    t5 = quad.map(_temp7);
    $[12] = quad;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t4 || $[15] !== t5) {
    t6 = <div className="grid grid-cols-4 gap-2 sm:gap-2.5 lg:gap-5">{t4}{t5}</div>;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t3 || $[18] !== t6) {
    t7 = <section className="flex flex-col gap-2.5">{t3}{t6}</section>;
    $[17] = t3;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}

// ── photoBanner → «Подборки» ───────────────────────────────────────────────
// A-10 (Айталы 02.10): подборки без прокрутки — по ширине страницы три плитки
// 16:9, подпись в левом нижнем углу (кадр 2871:30392 «Подборки»: 367×194, зазор
// 20, r4, 600/16 белым, отступ 10). На планшете и телефоне макет оставляет ряд
// с прокруткой: 288×152 с зазором 10 и 180×95 с зазором 8 (подпись 600/12/15,
// отступ 3), как в кадрах 3840:59825 и 2871:30391.
function _temp7(tile) {
  return <MosaicTile key={tile.id} tile={tile} className="aspect-square" />;
}
function _temp6(t) {
  return t.imageUrl;
}
export function PhotoBanner(t0) {
  const $ = _c(21);
  const {
    title,
    description,
    slides
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== description || $[1] !== slides || $[2] !== title) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const usable = slides.filter(_temp8);
      if (usable.length === 0) {
        t6 = null;
        break bb0;
      }
      t3 = "flex flex-col gap-2.5";
      t4 = "home-collections";
      if ($[9] !== description || $[10] !== title) {
        t5 = <SectionHeader title={title} description={description} />;
        $[9] = description;
        $[10] = title;
        $[11] = t5;
      } else {
        t5 = $[11];
      }
      if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = cn(RAIL_CLASS, "gap-2 sm:gap-2.5 lg:grid lg:grid-cols-3 lg:gap-5 lg:overflow-visible");
        $[12] = t1;
      } else {
        t1 = $[12];
      }
      t2 = usable.map(_temp9);
    }
    $[0] = description;
    $[1] = slides;
    $[2] = title;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($[13] !== t1 || $[14] !== t2) {
    t7 = <div className={t1}>{t2}</div>;
    $[13] = t1;
    $[14] = t2;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t3 || $[17] !== t4 || $[18] !== t5 || $[19] !== t7) {
    t8 = <section className={t3} data-testid={t4}>{t5}{t7}</section>;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}

// ── productRail → titled horizontal row of products ─────────────────────────
function _temp9(slide) {
  return <Link key={slide.id} to={targetHref(slide)} className="group relative aspect-[180/95] w-[180px] shrink-0 snap-start overflow-hidden rounded-[4px] bg-osmix-gray-100 outline-none focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40 sm:aspect-[288/152] sm:w-[288px] lg:aspect-video lg:w-auto"><img src={slide.imageUrl} alt={slide.title ?? ""} loading="lazy" className="size-full object-cover transition-transform duration-300 group-hover:scale-105" />{slide.title && <span className="absolute bottom-[3px] left-[3px] line-clamp-1 max-w-[calc(100%-6px)] text-[12px] font-semibold leading-[15px] text-white drop-shadow-sm sm:bottom-2.5 sm:left-2.5 sm:max-w-[calc(100%-20px)] sm:text-[16px] sm:leading-5">{slide.title}</span>}</Link>;
}
function _temp8(s) {
  return s.imageUrl;
}
export function ProductRail(t0) {
  const $ = _c(11);
  const {
    title,
    description,
    products
  } = t0;
  if (products.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== description || $[1] !== title) {
    t1 = <SectionHeader title={title} description={description} chevronHref="/catalog" />;
    $[0] = description;
    $[1] = title;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn(RAIL_CLASS, "gap-2 sm:gap-2.5 lg:gap-5");
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== products) {
    t3 = expandProductTiles(products).map(_temp0);
    $[4] = products;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <div className={t2}>{t3}</div>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t1 || $[9] !== t4) {
    t5 = <section className="flex flex-col gap-2.5" data-testid="home-product-rail">{t1}{t4}</section>;
    $[8] = t1;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
function _temp0(tile) {
  return <div key={tile.key} className="w-[166px] shrink-0 snap-start sm:w-[173px]"><ProductCard product={tile.product} tile={tile} variant="rail" /></div>;
}